import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { RefreshCw, Calendar, Users, Activity, AlertCircle, Bell, Receipt } from 'lucide-react';
import ScheduleCalendar from '../components/ScheduleCalendar';
import ClientScheduleCard from '../components/ClientScheduleCard';
import { useScheduleData } from '../hooks/useScheduleData';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import RoleBasedNavigation from '../components/RoleBasedNavigation';
import { AtomicLoadingScreen } from '../components/ui/atomic-loader';

const ClientSchedule: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const {
    currentDate,
    scheduleData,
    remindersData,
    reminderCalendarData,
    activeTab,
    setActiveTab,
    loading,
    error,
    updatingStatus,
    goToPreviousMonth,
    goToNextMonth,
    goToCurrentMonth,
    updateEventStatus,
    refreshData,
    getSortedClients,
    getRemindersByDay,
    calendarDays,
    monthStats,
    year,
    month
  } = useScheduleData();

  const [selectedDay, setSelectedDay] = useState<number>(new Date().getDate());

  // Verificar autenticação
  if (!isAuthenticated || !user) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600 mb-2">
              <AlertCircle className="h-5 w-5" />
              <span className="font-semibold">Acesso Negado</span>
            </div>
            <p className="text-red-600">Você precisa estar logado para acessar esta página.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Verificar se é cliente
  if (user.role !== 'cliente') {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-orange-600 mb-2">
              <AlertCircle className="h-5 w-5" />
              <span className="font-semibold">Acesso Restrito</span>
            </div>
            <p className="text-orange-600">Esta página é exclusiva para clientes.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Obter clientes ordenados para o dia selecionado (será apenas o próprio cliente) - apenas para cobranças
  const sortedClients = activeTab === 'COBRANÇA' ? getSortedClients(selectedDay) : [];

  // Verificar se há eventos no dia selecionado (apenas para cobranças)
  const hasEventsToday = activeTab === 'COBRANÇA' && sortedClients.length > 0 && sortedClients.some(client => {
    const events = client.events[selectedDay];
    return events && events.length > 0;
  });

  // Verificar se há lembretes no dia selecionado
  const remindersForDay = activeTab === 'LEMBRETE' ? getRemindersByDay(selectedDay) : [];
  const hasRemindersToday = activeTab === 'LEMBRETE' && remindersForDay.length > 0;

  const handleDayClick = (day: number) => {
    setSelectedDay(day);
  };

  const handleRefresh = () => {
    refreshData();
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <AtomicLoadingScreen 
              message="Carregando seu cronograma..." 
              className="h-64"
            />
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-red-600 mb-2">
                <AlertCircle className="h-5 w-5" />
                <span className="font-semibold">Erro ao carregar dados</span>
              </div>
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={handleRefresh} variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Tentar novamente
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <RoleBasedNavigation>
      <Layout>
        <div className="container mx-auto p-6 space-y-6">
          {/* Cabeçalho com Informações do Cliente */}
          <Card className="glass-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-montserrat">
                    Cronograma de Envios
                  </CardTitle>
                  <p className="text-gray-600 mt-1">
                    {user.client?.legal_name} - {user.client?.trade_name}
                  </p>
                </div>
                <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                  <Calendar className="w-8 h-8 text-white" />
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Toggle Button para Cobranças/Lembretes */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Button
                  variant={activeTab === 'COBRANÇA' ? 'default' : 'ghost'}
                  onClick={() => setActiveTab('COBRANÇA')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                    activeTab === 'COBRANÇA'
                      ? 'bg-orange-500 hover:bg-orange-600 text-white font-bold'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <Receipt className="w-4 h-4" />
                  Cobrança
                </Button>
                
                <Button
                  variant={activeTab === 'LEMBRETE' ? 'default' : 'ghost'}
                  onClick={() => setActiveTab('LEMBRETE')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all ${
                    activeTab === 'LEMBRETE'
                      ? 'bg-orange-500 hover:bg-orange-600 text-white font-bold'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <Bell className="w-4 h-4" />
                  Lembrete
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Seção do Calendário e Detalhes - COBRANÇA */}
          {activeTab === 'COBRANÇA' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendário */}
            <div className="lg:col-span-1">
              <ScheduleCalendar
                currentDate={currentDate}
                calendarDays={calendarDays}
                onPreviousMonth={goToPreviousMonth}
                onNextMonth={goToNextMonth}
                onCurrentMonth={goToCurrentMonth}
                onDayClick={handleDayClick}
                selectedDay={selectedDay}
              />
            </div>

            {/* Detalhes do Dia Selecionado */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      Dia {selectedDay} - {new Date(year, month - 1, selectedDay).toLocaleDateString('pt-BR', { 
                        weekday: 'long', 
                        day: 'numeric', 
                        month: 'long',
                        year: 'numeric' 
                      })}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {hasEventsToday && (
                        <Badge variant="outline" className="bg-green-100 text-green-600">
                          Com eventos
                        </Badge>
                      )}
                      {!hasEventsToday && (
                        <Badge variant="outline" className="bg-gray-100 text-gray-600">
                          Sem eventos
                        </Badge>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={loading}
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Atualizar
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {sortedClients.length === 0 ? (
                    <div className="text-center py-8">
                      <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">Nenhum evento cadastrado para este mês</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {sortedClients.map((clientData) => (
                        <ClientScheduleCard
                          key={`${clientData.client.id}-${selectedDay}`}
                          clientData={clientData}
                          day={selectedDay}
                          currentDate={{ year, month }}
                          onStatusChange={updateEventStatus}
                          updatingStatus={updatingStatus}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
          )}

          {/* Seção de Lembretes com Calendário */}
          {activeTab === 'LEMBRETE' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendário */}
            <div className="lg:col-span-1">
              <ScheduleCalendar
                currentDate={currentDate}
                calendarDays={calendarDays}
                onPreviousMonth={goToPreviousMonth}
                onNextMonth={goToNextMonth}
                onCurrentMonth={goToCurrentMonth}
                onDayClick={handleDayClick}
                selectedDay={selectedDay}
              />
            </div>

            {/* Seção dos Cards de Lembretes do Dia */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="h-5 w-5" />
                      Lembretes - Dia {selectedDay}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-orange-100 text-orange-600">
                        {remindersForDay.length} lembretes
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRefresh}
                        disabled={loading}
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Atualizar
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {remindersForDay.length === 0 ? (
                    <div className="text-center py-8">
                      <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">Nenhum lembrete programado para o dia {selectedDay}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        Os lembretes aparecem nos dias de envio (data de vencimento - dias antes)
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {remindersForDay.map((reminder, index) => (
                        <div
                          key={`reminder-${reminder.reminderRuleId}-${reminder.dueDateId}-${index}`}
                          className="p-4 border border-orange-200 rounded-lg bg-orange-50"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                  <span className="text-blue-600 font-semibold text-sm">
                                    {(reminder.client.trade_name || reminder.client.legal_name).charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {reminder.client.trade_name || reminder.client.legal_name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {reminder.client.email}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="ml-12 space-y-1">
                                <div className="flex items-center gap-2 text-sm">
                                  <Bell className="h-4 w-4 text-orange-500" />
                                  <span className="font-medium text-orange-700">
                                    Hoje, dia {selectedDay.toString().padStart(2, '0')}/{month.toString().padStart(2, '0')} terá envio para o vencimento do dia {reminder.dueDay.toString().padStart(2, '0')}/{month.toString().padStart(2, '0')}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4 text-xs text-gray-600">
                                  <span>📅 {reminder.daysBeforeDue} dia(s) antes</span>
                                  <span>🕐 Horário: {reminder.windowStart}</span>
                                </div>
                              </div>
                            </div>
                            
                            <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                              LEMBRETE
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
          )}

          {/* Informações de Uso */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-blue-600 text-xs font-semibold">i</span>
                </div>
                <div>
                  <h4 className="font-semibold text-blue-800 mb-1">Como usar o Cronograma</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• <strong>Abas:</strong> Use as abas "Cobrança" e "Lembrete" para alternar entre os tipos de envio</li>
                    <li>• <strong>Dias destacados:</strong> Dias com eventos programados aparecem em verde no calendário</li>
                    <li>• <strong>Clique nos dias:</strong> Selecione um dia para ver os detalhes dos eventos programados</li>
                    <li>• <strong>Marcar progresso:</strong> Use os checkboxes "Em andamento" e "Finalizado" para acompanhar o status (apenas cobranças)</li>
                    <li>• <strong>Navegação:</strong> Use as setas para navegar entre os meses ou clique em "Hoje" para voltar ao mês atual</li>
                    <li>• <strong>Vencimento:</strong> A data de vencimento do boleto é calculada automaticamente baseada na regra</li>
                    <li>• <strong>Lembretes:</strong> Os lembretes aparecem nos dias de envio calculados (data de vencimento - dias antes)</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    </RoleBasedNavigation>
  );
};

export default ClientSchedule;


